import { Fragment, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, StatTile, TrendPill } from '../../../components/ui/Card';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { Table, TableFooter, TGroupRow, THead, Th, Td, Tr, TTotalRow } from '../../../components/ui/Table';
import { Select } from '../../../components/ui/Input';
import { useToolCosts, useSaveVendorMapping } from '../../../hooks/useFinance';
import type {
  ToolCostCategoryId,
  ToolCostMonthPoint,
  ToolCosts,
  ToolCostVendor,
  UnmappedVendor,
} from '../../../api/finance';
import {
  money,
  moneyAxis,
  formatMonthKey,
  humanizeMonthKeys,
  ledgerDisplayName,
  niceTicks,
  vendorDisplayName,
  fullMonths,
  isCurrentMonth,
  monthLabel,
  monthSpan,
  monthTick,
  plusMoney,
} from './formatters';
import { ChartKey } from './ChartKey';
import { TileSkeleton } from './TileSkeleton';
import { PanelTitle, useFinanceIssues } from './DataIssues';
import { vendorSpikes, type VendorSpike } from './toolCostSpikes';
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  BAR_RADIUS_VERTICAL,
  CHART_SERIES,
} from '../../../components/ui/chart';

const INSIGHTS_SHOWN = 3;
/** Up to this many months (the 3M window: three full months + month to date), the table shows them all. */
const TABLE_MAX_MONTHS = 4;

/**
 * One colour per cost category, by its place in the API's fixed category list,
 * so the chart's stacks, its key and the table's group dots name the same six
 * categories the filter tabs list. Ink and warm neutrals only, dark and light
 * alternating so neighbouring stacks stay apart: indigo is for links and
 * selection, and green, amber and red stay reserved for status.
 */
const CATEGORY_PALETTE = [
  CHART_SERIES[0], // ink        Hosting & License
  CHART_SERIES[4], // sand       IT, internal use
  CHART_SERIES[1], // umber      IT, product
  CHART_SERIES[6], // mist       Marketing tools
  CHART_SERIES[3], // slate      Sales tools: blue-grey, not a second near-black
  CHART_SERIES[2], // stone      Online Publicity (Ads)
] as const;

/**
 * The third tile counts only fully booked months, so it is named for them
 * rather than for the window ("Last 3 full months" over two booked months).
 */
const FULLY_BOOKED_TILE = 'Fully booked months';

/** Closed and every recurring bill booked. Older payloads without the flag count as booked. */
function isFullyBooked(p: ToolCostMonthPoint): boolean {
  return !isCurrentMonth(p.month) && p.fullyBooked !== false;
}

/**
 * The months an average or a comparison may use: full months (not the running
 * one, not a half first month) with every recurring vendor's bill booked.
 */
function bookedMonths(data: ToolCosts): ToolCostMonthPoint[] {
  return fullMonths(data.series, data.firstBillDate).filter(isFullyBooked);
}

/**
 * "OpenAI, usually €7,074; Amazon Web Services, €4,490 and 5 more". No
 * brackets of its own: the list often sits inside a parenthesis already, and
 * "(LinkedIn Sales Navigator (usually €62))" read badly.
 */
function missingList(p: ToolCostMonthPoint, currency: string, shown = 3): string {
  const missing = p.missingBills ?? [];
  const named = missing
    .slice(0, shown)
    .map((m, i) => `${vendorDisplayName(m.vendor)}, ${i === 0 ? 'usually ' : ''}${money(m.expected, currency)}`);
  const rest = missing.length - shown;
  if (rest > 0) return `${named.join('; ')} and ${rest} more`;
  return named.length > 1 ? `${named.slice(0, -1).join('; ')} and ${named[named.length - 1]}` : (named[0] ?? '');
}

/** "2026-09" → "2026-10". */
function nextMonthKey(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/**
 * A missing bill whose vendor carries about two months in the month after
 * (AWS: no bill in Sep 26, €8,358 in Oct against a usual €4,490). Odoo books a
 * bill on its accounting date, and a late-entered bill lands in the month it
 * was booked, not the month it is for. Said as likely, not asserted. Pure.
 */
export function likelyBookedLater(
  vendors: ToolCosts['vendors'],
  p: ToolCostMonthPoint,
): { vendor: string; month: string; amount: number; expected: number }[] {
  const next = nextMonthKey(p.month);
  return (p.missingBills ?? []).flatMap((m) => {
    const amount = vendors.filter((v) => v.vendor === m.vendor).reduce((sum, v) => sum + (v.byMonth[next] ?? 0), 0);
    // Small bills (under €250 a month) are left to the list above: a doubled €62 is noise.
    return m.expected >= 250 && amount >= m.expected * 1.6 ? [{ vendor: m.vendor, month: next, amount, expected: m.expected }] : [];
  });
}

/**
 * Vendor months over 3x their usual that no "looks booked in" note already
 * explains (that note covers a bill from the month before landing late).
 */
export function unexplainedSpikes(data: ToolCosts): VendorSpike[] {
  const current = data.series.find((p) => isCurrentMonth(p.month))?.month ?? null;
  const explained = new Set(
    data.series.flatMap((p) => likelyBookedLater(data.vendors, p).map((l) => `${l.vendor}:${l.month}`)),
  );
  return vendorSpikes(
    data.vendors,
    data.series.map((p) => p.month),
    current,
  ).filter((sp) => !explained.has(`${sp.vendor}:${sp.month}`));
}

/** The window the unusual-month check is measured on, independent of the period chip. */
const BASELINE_MONTHS_SHOWN = 12;

/** Smooth-scroll to a card on the page without touching the URL. */
function jumpTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** What needs a human, at the top: unmapped vendors, mis-booked bills, a vendor far over its usual. */
function AttentionLine({ data, baseline }: { data: ToolCosts; baseline: ToolCosts }) {
  const unmapped = data.unmapped.length;
  const misbooked = data.misbooked.filter((m) => Math.abs(m.total) >= MISBOOKED_MIN_EUR).length;
  // Spikes are measured on a fixed baseline (the last 12 months), whatever the
  // period chip says, so the list does not change with it. Only the last three
  // closed months are news; older ones stay marked in the table.
  const recent = new Set(baseline.series.filter((p) => !isCurrentMonth(p.month)).slice(-3).map((p) => p.month));
  const allSpikes = unexplainedSpikes(baseline).filter((sp) => recent.has(sp.month));
  const spikes = allSpikes.slice(0, 3);
  if (unmapped === 0 && misbooked === 0 && spikes.length === 0) return null;
  const link = 'font-medium text-text-primary underline decoration-border underline-offset-2 hover:decoration-text-primary';
  return (
    <div className="-mt-2 space-y-1 text-body-sm text-text-secondary">
      {(unmapped > 0 || misbooked > 0) && (
        <p>
          <span className="text-text-tertiary">To fix in the mapping: </span>
          {unmapped > 0 && (
            <button type="button" className={link} onClick={() => jumpTo('tool-unmapped')}>
              {unmapped} unmapped
            </button>
          )}
          {unmapped > 0 && misbooked > 0 && ', '}
          {misbooked > 0 && (
            <button type="button" className={link} onClick={() => jumpTo('tool-misbooked')}>
              {misbooked} mis-booked
            </button>
          )}
          <span className="text-text-tertiary">
            {' '}
            (vendors with spend in the {data.series.length} months shown; tables at the foot of the page)
          </span>
        </p>
      )}
      {spikes.length > 0 && <p className="eyebrow pt-1">Unusual months · last 3 closed months, against each vendor&apos;s usual over 12 months</p>}
      {spikes.map((sp) => (
        <p key={`${sp.vendor}:${sp.month}`} className="flex items-start gap-2">
          <span aria-hidden className="mt-2 h-2 w-2 shrink-0 rounded-full bg-warning" />
          <span>
            {vendorDisplayName(sp.vendor)} in {formatMonthKey(sp.month)}: {money(sp.amount, data.currency)}, about{' '}
            {Math.round(sp.amount / sp.usual)} times its usual {money(sp.usual, data.currency)} (median of its earlier
            months).
          </span>
        </p>
      ))}
      {allSpikes.length > spikes.length && (
        <p className="text-text-tertiary">
          {allSpikes.length - spikes.length} more vendor month{allSpikes.length - spikes.length === 1 ? '' : 's'} over 3
          times usual, marked in amber in the table below.
        </p>
      )}
    </div>
  );
}

function categoryColor(data: ToolCosts, id: ToolCostCategoryId): string {
  const i = Math.max(0, data.categories.findIndex((c) => c.id === id));
  return CATEGORY_PALETTE[i % CATEGORY_PALETTE.length];
}

function CategoryDot({ color }: { color: string }) {
  return <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />;
}

/**
 * Vendor name as people say it, with the Odoo name on hover. No lettered tile:
 * with no logo to show, a column of initials was clutter, not recognition.
 */
function VendorName({ raw, aliases, children }: { raw: string; aliases?: string[]; children?: React.ReactNode }) {
  const name = vendorDisplayName(raw);
  // Merged vendors list every name their bills carried; otherwise the Odoo name.
  const odooNames = aliases && aliases.length > 0 ? aliases.join(' · ') : name !== raw ? raw : undefined;
  return (
    <span className="flex min-w-0 items-center gap-2" title={odooNames}>
      <span className="truncate text-text-primary">{name}</span>
      {children}
    </span>
  );
}

/**
 * An Odoo ledger account: its code in mono, its Dutch all-caps name in sentence
 * case (the original on hover), and the English category it stands for when
 * the name alone doesn't say it.
 */
function LedgerAccount({ code, name, english }: { code: string; name: string; english?: string }) {
  // The account's name, English first; the code and the Odoo (Dutch) name sit behind the tooltip.
  return <span title={`${code} · ${name}`}>{english ?? ledgerDisplayName(name)}</span>;
}

/** Accounts outside the six tool categories that bookings still land on. */
const OTHER_ACCOUNT_LABELS: Record<string, string> = {
  '602000': 'Purchased services',
};

/** The English label for an account code: the category it stands for, else a known account. */
function englishFor(data: ToolCosts, code: string): string | undefined {
  return data.categories.find((c) => c.accountCode === code)?.label ?? OTHER_ACCOUNT_LABELS[code];
}

/** Insight prose names vendors by their Odoo name; say them the way the table does. */
function insightText(message: string, vendor: string | null): string {
  const text = humanizeMonthKeys(message);
  return vendor ? text.split(vendor).join(vendorDisplayName(vendor)) : text;
}

function InsightsCard({ data }: { data: ToolCosts }) {
  const [expanded, setExpanded] = useState(false);
  if (!data.insightsMonth) return null;
  // Warnings first, so the three shown are the ones worth reading.
  const sorted = [...data.insights].sort(
    (a, b) => Number(b.severity === 'warning') - Number(a.severity === 'warning'),
  );
  const shown = expanded ? sorted : sorted.slice(0, INSIGHTS_SHOWN);
  return (
    <Card padding="md">
      <PanelTitle
        title={`Insights for ${formatMonthKey(data.insightsMonth)}`}
        info="Compared against the average of the three fully booked months before it. A month is fully booked once the bills still missing from recurring vendors (billed in 2 of the 3 months before, €50 or more) come to under 2% of a usual month; later months are left out until then. One-off annual charges are left out of the category comparison."
      />
      {data.insights.length === 0 ? (
        <p className="text-body-sm text-text-tertiary">
          Nothing strange: every category and vendor is in its normal range.
        </p>
      ) : (
        // A plain list, not a stack of alert boxes: these are observations about
        // spend, and boxing each one made the tab read like a page of errors.
        <ul className="divide-y divide-border-light">
          {shown.map((insight, idx) => (
            <li key={idx} className="flex items-start gap-2.5 py-2 text-body-sm text-text-secondary">
              <span
                aria-hidden
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  insight.severity === 'warning' ? 'bg-warning' : 'bg-border'
                }`}
              />
              {insightText(insight.message, insight.vendor)}
            </li>
          ))}
        </ul>
      )}
      {sorted.length > INSIGHTS_SHOWN && (
        <ShowAllToggle expanded={expanded} total={sorted.length} onToggle={() => setExpanded((v) => !v)} />
      )}
    </Card>
  );
}

/**
 * The tab used to look authoritative while running entirely on a fallback. The
 * mapping warnings go to the page's data-notes chip, where they stay visible.
 */
function useHealthNotes(data: ToolCosts | undefined) {
  useFinanceIssues(
    'tools.health',
    data?.health.degraded
      ? data.health.warnings.map((message) => ({ code: 'tool_costs_mapping', severity: 'warning' as const, message }))
      : undefined,
  );
}

interface StackTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: readonly { payload?: Record<string, unknown> }[];
  stacks: { key: string; label: string; color: string }[];
  currency: string;
}

/**
 * The month's categories, top of the stack first. Recharts' default colours each
 * line in its series colour, which left stone and lavender lines unreadable on
 * white; here the text is ink and the colour is a dot beside it.
 */
function StackTooltip({ active, label, payload, stacks, currency }: StackTooltipProps) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const rows = [...stacks]
    .reverse()
    .map((s) => ({ ...s, value: Number(point[s.key] ?? 0) }))
    .filter((r) => r.value !== 0);
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  return (
    <div className="rounded border border-border-light bg-surface-primary px-3 py-2 text-body-xs shadow-medium tabular-nums">
      <p className="mb-1 font-medium text-text-primary">{monthLabel(String(label))}</p>
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2">
            <CategoryDot color={r.color} />
            <span className="text-text-secondary">{r.label}</span>
            <span className="ml-auto pl-4 text-text-primary">{money(r.value, currency)}</span>
          </li>
        ))}
      </ul>
      {rows.length > 1 && (
        <p className="mt-1 flex justify-between gap-4 border-t border-border-light pt-1 font-medium text-text-primary">
          <span>Total</span>
          <span>{money(total, currency)}</span>
        </p>
      )}
    </div>
  );
}

function MonthlyStackedChart({
  data,
  action,
  categoryFilter,
}: {
  data: ToolCosts;
  action?: React.ReactNode;
  /** The vendor table's category chips; the chart shows the same category. */
  categoryFilter: ToolCostCategoryId | 'all';
}) {
  const categories =
    categoryFilter === 'all' ? data.categories : data.categories.filter((c) => c.id === categoryFilter);
  const totalOf = (p: ToolCosts['series'][number]) =>
    categoryFilter === 'all' ? p.total : p.byCategory[categoryFilter];
  // Months before the first booked bill are empty, not zero spend — leaving
  // them in pushed the real bars to the right half of the chart.
  const firstWithData = data.series.findIndex((p) => p.total !== 0);
  const series = firstWithData > 0 ? data.series.slice(firstWithData) : data.series;

  // Every category keeps its own stack, biggest over the window at the bottom.
  const ranked = [...categories].sort(
    (a, b) =>
      series.reduce((sum, p) => sum + p.byCategory[b.id], 0) - series.reduce((sum, p) => sum + p.byCategory[a.id], 0),
  );
  const stacks = ranked.map((c) => ({ key: c.id as string, label: c.label, color: categoryColor(data, c.id) }));
  const chartData = series.map((p) => ({
    month: p.month,
    ...Object.fromEntries(categories.map((c) => [c.id, p.byCategory[c.id]])),
  }));
  const scale = niceTicks(series.map(totalOf), 4);

  // A month missing a recurring vendor's bill is still filling up, and the
  // running month is only month-to-date. The bars stay at full strength (faded
  // ones were unreadable); the note under the chart says which months are WIP.
  const openPoints = series.filter((p) => !isFullyBooked(p));
  return (
    <Card padding="md">
      <PanelTitle
        title="Monthly tool spend per category"
        info={
          data.health.mappingSize === 0
            ? "Vendor bills grouped by the cost account Odoo booked them on. The vendor mapping is empty, so nothing here is actually mapped. Recent months may still be missing bills that aren't booked yet."
            : 'Vendor bills grouped by cost account, using the vendor mapping rather than the account the bill was booked on. Months marked WIP below the chart are not fully booked yet: a recurring vendor (billed in 2 of the 3 months before, €50 or more) has no bill in them. The running month is marked MTD (month to date). The category chips under "Spend per tool" filter this chart too.'
        }
        action={action}
      />
      {data.series.every((p) => totalOf(p) === 0) ? (
        <EmptyState compact title="No tool costs in this window yet" />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT.tall}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="month" tickFormatter={monthTick} {...CHART_AXIS} />
              <YAxis
                domain={scale.domain}
                ticks={scale.ticks}
                tickFormatter={(v) => moneyAxis(Number(v), data.currency)}
                width={56}
                {...CHART_AXIS}
              />
              <Tooltip
                cursor={{ fill: 'rgb(18 18 18 / 0.04)' }}
                content={(props) => <StackTooltip {...props} stacks={stacks} currency={data.currency} />}
              />
              {stacks.map((stack, i) => (
                <Bar
                  key={stack.key}
                  dataKey={stack.key}
                  name={stack.label}
                  stackId="tools"
                  fill={stack.color}
                  maxBarSize={48}
                  // Only the topmost segment of a stack gets the rounded cap.
                  radius={i === stacks.length - 1 ? [...BAR_RADIUS_VERTICAL] : undefined}
                  isAnimationActive={false}
                >
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <ChartKey items={categories.map((c) => ({ label: c.label, color: categoryColor(data, c.id) }))} />
          {openPoints.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-body-xs text-text-tertiary">
              {openPoints.map((p) => (
                <li key={p.month}>
                  {isCurrentMonth(p.month)
                    ? `${formatMonthKey(p.month)} is the running month: month to date only (WIP).`
                    : `${formatMonthKey(p.month)} is not complete yet (WIP): bills still missing from ${missingList(p, data.currency, 4) || 'recurring vendors'}.`}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** Below half a euro over the window a vendor has no spend worth a row (Customer.io nets to €0). */
const ZERO_SPEND_EUR = 0.5;

function VendorsCard({
  data,
  baseline,
  categoryFilter,
  setCategoryFilter,
}: {
  data: ToolCosts;
  /** The fixed 12-month view the amber marks are measured on, so they do not move with the chip. */
  baseline: ToolCosts;
  categoryFilter: ToolCostCategoryId | 'all';
  setCategoryFilter: (id: ToolCostCategoryId | 'all') => void;
}) {
  const options = useMemo(
    () => [{ id: 'all' as const, label: 'All' }, ...data.categories.map((c) => ({ id: c.id, label: c.label }))],
    [data.categories]
  );

  const [showQuiet, setShowQuiet] = useState(false);
  const spikeAt = useMemo(() => new Map(unexplainedSpikes(baseline).map((sp) => [`${sp.vendor}:${sp.month}`, sp])), [baseline]);
  // The 3M window (three full months and the month to date) shows every month
  // it holds; longer windows show their last three. Either way the Total
  // column adds up exactly the months on screen.
  const lastPoints = useMemo(
    // The same four months on every window (3M is exactly four), so the table never changes shape with the chip.
    () => data.series.slice(-TABLE_MAX_MONTHS),
    [data.series],
  );
  const lastMonths = useMemo(() => lastPoints.map((p) => p.month), [lastPoints]);
  // Average over fully booked full months only: not the month-to-date, not a
  // half first month, not a month still missing a recurring bill.
  const avgMonths = bookedMonths(data).map((p) => p.month);
  const perMonth = (v: ToolCostVendor) => avgMonths.reduce((sum, m) => sum + (v.byMonth[m] ?? 0), 0);
  const avgDivisor = Math.max(1, avgMonths.length);
  const hiddenZero = data.vendors.filter((v) => Math.abs(v.total) < ZERO_SPEND_EUR).length;
  // A row of dots in every month shown says nothing; its spend is in the total.
  const isQuiet = (v: ToolCostVendor) => lastMonths.every((m) => !v.byMonth[m]);
  const quiet = data.vendors.filter(
    (v) =>
      Math.abs(v.total) >= ZERO_SPEND_EUR &&
      isQuiet(v) &&
      (categoryFilter === 'all' || v.categoryId === categoryFilter),
  );
  const quietTotal = quiet.reduce((sum, v) => sum + v.total, 0);
  /** A vendor's spend over the month columns shown, which is what the Total column means. */
  const shownTotal = (v: ToolCostVendor) => lastMonths.reduce((sum, m) => sum + (v.byMonth[m] ?? 0), 0);
  const showsWholeWindow = lastPoints.length === data.series.length;

  /** One group per category, biggest first, each with its vendors and subtotals. */
  const groups = useMemo(() => {
    const byCategory = new Map<ToolCostCategoryId, ToolCostVendor[]>();
    for (const v of data.vendors) {
      // A vendor that nets to €0 in the window is a display row with nothing in it.
      if (Math.abs(v.total) < ZERO_SPEND_EUR) continue;
      if (categoryFilter !== 'all' && v.categoryId !== categoryFilter) continue;
      if (!showQuiet && lastMonths.every((m) => !v.byMonth[m])) continue;
      byCategory.set(v.categoryId, [...(byCategory.get(v.categoryId) ?? []), v]);
    }
    return data.categories
      .filter((c) => byCategory.has(c.id))
      .map((c) => {
        const shown = (v: ToolCostVendor) => lastMonths.reduce((sum, m) => sum + (v.byMonth[m] ?? 0), 0);
        const vendors = byCategory.get(c.id)!.slice().sort((a, b) => shown(b) - shown(a));
        return { category: c, vendors, total: vendors.reduce((sum, v) => sum + shown(v), 0) };
      })
      .sort((a, b) => b.total - a.total);
  }, [data.vendors, data.categories, categoryFilter, showQuiet, lastMonths]);

  // The /mo figures count every vendor in the category, hidden ones included,
  // so the footer's average is the KPI tile's average for the same months.
  const inFilter = data.vendors.filter((v) => categoryFilter === 'all' || v.categoryId === categoryFilter);
  const avgPerMonth = (vendors: ToolCostVendor[]) => vendors.reduce((sum, v) => sum + perMonth(v), 0) / avgDivisor;
  const categoryAvg = (id: ToolCostCategoryId) => avgPerMonth(data.vendors.filter((v) => v.categoryId === id));
  const hiddenAvg = avgPerMonth(quiet);
  const vendorCount = groups.reduce((n, g) => n + g.vendors.length, 0);
  const grandTotal = groups.reduce((sum, g) => sum + g.total, 0);
  const monthSum = (vendors: ToolCostVendor[], m: string) => vendors.reduce((sum, v) => sum + (v.byMonth[m] ?? 0), 0);
  const colSpan = 2 + lastMonths.length;

  return (
    <Card padding="md">
      <PanelTitle
        title={showsWholeWindow ? 'Spend per tool' : `Spend per tool, last ${lastMonths.length} months shown`}
        info={
          <>
            Grouped by cost category. Vendors marked <span className="italic">unmapped</span> were booked on a tool
            account but aren&apos;t in the cost mapping yet; <span className="italic">annual</span> means one charge in
            the last year, left out of the month-on-month comparisons. Hover a vendor for its name in Odoo, or the
            names merged into it. Months still missing a recurring bill are marked with an asterisk.
          </>
        }
      />
      <div className="mb-3 overflow-x-auto">
        <SegmentedControl ariaLabel="Category" options={options} value={categoryFilter} onChange={setCategoryFilter} />
      </div>

      {groups.length === 0 ? (
        <EmptyState compact title="No vendors in this category yet" />
      ) : (
        <>
          <Table minWidth={640} colWidths={['auto', ...lastMonths.map(() => '8rem'), '9rem']}>
            <THead>
              <tr>
                <Th>Vendor</Th>
                {lastPoints.map((p) => (
                  <Th key={p.month} align="right">
                    {isFullyBooked(p) ? (
                      formatMonthKey(p.month)
                    ) : (
                      <span
                        title={
                          isCurrentMonth(p.month)
                            ? 'Month to date'
                            : `Not fully booked: no bill yet from ${missingList(p, data.currency, 10)}`
                        }
                      >
                        {isCurrentMonth(p.month) ? `${formatMonthKey(p.month)} MTD` : `${formatMonthKey(p.month)}*`}
                      </span>
                    )}
                  </Th>
                ))}
                <Th align="right">Total {monthSpan(lastMonths)}</Th>
              </tr>
            </THead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.category.id}>
                  <TGroupRow
                    colSpan={colSpan}
                    label={g.category.label}
                    icon={<CategoryDot color={categoryColor(data, g.category.id)} />}
                    // Whole euros like every other amount in the table, not "€12.2k" beside "€4,254".
                    // Each figure names its own months: the average is over the fully booked ones.
                    summary={`${g.vendors.length} vendor${g.vendors.length === 1 ? '' : 's'} · ${money(
                      g.total,
                      data.currency,
                    )} in ${monthSpan(lastMonths)}${
                      avgMonths.length > 0
                        ? ` · avg ${money(categoryAvg(g.category.id), data.currency)}/mo over ${monthSpan(avgMonths)}`
                        : ''
                    }`}
                  />
                  {g.vendors.map((vendor) => (
                    <Tr key={`${vendor.categoryId}:${vendor.vendor}`}>
                      <Td>
                        <VendorName raw={vendor.vendor} aliases={vendor.aliases}>
                          {!vendor.mapped && <span className="shrink-0 text-body-xs italic text-text-tertiary">unmapped</span>}
                          {vendor.annual && <span className="shrink-0 text-body-xs italic text-text-tertiary">annual</span>}
                        </VendorName>
                      </Td>
                      {lastMonths.map((m) => (
                        <Td key={m} align="right" className="tabular-nums text-text-secondary">
                          {vendor.byMonth[m] ? (
                            spikeAt.has(`${vendor.vendor}:${m}`) ? (
                              <span
                                className="font-medium text-warning-dark"
                                title={`About ${Math.round(vendor.byMonth[m]! / spikeAt.get(`${vendor.vendor}:${m}`)!.usual)} times its usual ${money(spikeAt.get(`${vendor.vendor}:${m}`)!.usual, data.currency)}`}
                              >
                                {money(vendor.byMonth[m]!, data.currency)}
                              </span>
                            ) : (
                              money(vendor.byMonth[m]!, data.currency)
                            )
                          ) : null}
                        </Td>
                      ))}
                      <Td align="right" className="tabular-nums font-medium text-text-primary">
                        {money(shownTotal(vendor), data.currency)}
                      </Td>
                    </Tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <TTotalRow>
                <Td>Total</Td>
                {lastMonths.map((m) => (
                  <Td key={m} align="right" className="tabular-nums">
                    {money(groups.reduce((sum, g) => sum + monthSum(g.vendors, m), 0), data.currency)}
                  </Td>
                ))}
                <Td align="right" className="tabular-nums">{money(grandTotal, data.currency)}</Td>
              </TTotalRow>
            </tfoot>
          </Table>
          <TableFooter
            action={
              quiet.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setShowQuiet((v) => !v)}
                  className="text-body-xs font-medium text-text-tertiary hover:text-text-primary"
                >
                  {showQuiet ? 'Hide them' : `Show ${quiet.length}`}
                </button>
              ) : undefined
            }
          >
            {vendorCount} vendors in {groups.length} categor{groups.length === 1 ? 'y' : 'ies'} ·{' '}
            {money(avgPerMonth(inFilter), data.currency)} a month on average
            {avgMonths.length > 0 ? ` over the fully booked months ${monthSpan(avgMonths)}` : ''}
            {categoryFilter === 'all' ? ', the same figure as the tile above' : ''}
            {!showQuiet &&
              quiet.length > 0 &&
              ` · ${quiet.length} vendor${quiet.length === 1 ? '' : 's'} with no bill in ${monthSpan(lastMonths)} hidden: in the average (${money(hiddenAvg, data.currency)}/mo), not in the table (${money(quietTotal, data.currency)} ${showsWholeWindow ? 'in this window' : `before ${formatMonthKey(lastMonths[0]!)}`})`}
            {hiddenZero > 0 && ` · ${hiddenZero} vendor${hiddenZero === 1 ? '' : 's'} with €0 net spend hidden`}
          </TableFooter>
        </>
      )}
    </Card>
  );
}

/** The report's name for the supplier, with the name on the bills on hover when it differs. */
function UnmappedVendorName({ vendor }: { vendor: UnmappedVendor }) {
  const shown = vendor.displayName ?? vendor.vendor;
  return <VendorName raw={shown} aliases={shown !== vendor.vendor ? [vendor.vendor] : undefined} />;
}

/** The accounts a vendor was booked on, by name; the codes are in the tooltip. */
function BookedOn({ codes, data }: { codes: string[]; data: ToolCosts }) {
  if (codes.length === 0) return null;
  return <span title={codes.join(', ')}>{codes.map((c) => englishFor(data, c) ?? `Account ${c}`).join(', ')}</span>;
}

const EXCLUDE_VALUE = 'exclude';
const SPLIT_VALUE = 'split';

function UnmappedRow({ vendor, data }: { vendor: UnmappedVendor; data: ToolCosts }) {
  const save = useSaveVendorMapping();
  const [choice, setChoice] = useState('');

  // A card-paid tool has no Odoo supplier record, so its mapping is keyed on
  // the name the booking carries. Thirteen vendors sat permanently unmapped
  // because this row used to refuse to offer them a category at all.
  const nameOnly = vendor.odooPartnerId === null;
  const tooShortToMatch = nameOnly && vendor.vendor.trim().length < 3;
  const canSplit = vendor.bookedAccountCodes.length > 1;

  if (tooShortToMatch) {
    return (
      <Tr>
        <Td><UnmappedVendorName vendor={vendor} /></Td>
        <Td className="text-text-secondary"><BookedOn codes={vendor.bookedAccountCodes} data={data} /></Td>
        <Td align="right" className="tabular-nums font-medium">{money(vendor.total, data.currency)}</Td>
        <Td className="text-body-xs text-text-tertiary">
          No supplier on the booking and the description is too short to match on. Fix it in Odoo.
        </Td>
      </Tr>
    );
  }

  const identity = {
    odooPartnerId: vendor.odooPartnerId,
    vendorName: vendor.vendor,
    ...(nameOnly ? { matchName: vendor.vendor } : {}),
  };

  const onAssign = (value: string) => {
    setChoice(value);
    if (!value) return;

    // One supplier billing across accounts (LinkedIn: Sales Navigator and Ads)
    // becomes one row per account, each scoped to the account it was booked on.
    if (value === SPLIT_VALUE) {
      for (const accountCode of vendor.bookedAccountCodes) {
        save.mutate({ ...identity, accountCode, whenAccountCode: accountCode });
      }
      return;
    }

    save.mutate({
      ...identity,
      // Excluded vendors still need an account column; its booked account is the honest one.
      accountCode:
        value === EXCLUDE_VALUE
          ? vendor.bookedAccountCodes[0] ?? data.categories[0]!.accountCode
          : value,
      isExcluded: value === EXCLUDE_VALUE,
    });
  };

  return (
    <Tr>
      <Td><UnmappedVendorName vendor={vendor} /></Td>
      <Td className="text-text-secondary"><BookedOn codes={vendor.bookedAccountCodes} data={data} /></Td>
      <Td align="right" className="tabular-nums font-medium">{money(vendor.total, data.currency)}</Td>
      <Td>
        <div className="flex items-center gap-2">
          <Select
            size="sm"
            aria-label={`Category for ${vendorDisplayName(vendor.displayName ?? vendor.vendor)}`}
            value={choice}
            disabled={save.isPending || save.isSuccess}
            onChange={(e) => onAssign(e.target.value)}
            className="max-w-full"
          >
            <option value="">Assign to…</option>
            {data.categories.map((c) => (
              <option key={c.id} value={c.accountCode}>
                {c.label}
              </option>
            ))}
            {canSplit && (
              <option value={SPLIT_VALUE}>
                Split: keep each of {vendor.bookedAccountCodes.map((c) => englishFor(data, c) ?? `account ${c}`).join(', ')} separate
              </option>
            )}
            <option value={EXCLUDE_VALUE}>Not tool spend, exclude</option>
          </Select>
          {save.isPending && <span className="text-body-xs text-text-tertiary">Saving…</span>}
          {save.isSuccess && <span className="text-body-xs text-success">Saved</span>}
          {save.isError && (
            <span className="text-body-xs text-error">{save.error.message || 'Failed to save'}</span>
          )}
        </div>
      </Td>
    </Tr>
  );
}

function ShowAllToggle({ expanded, total, onToggle }: { expanded: boolean; total: number; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-2 text-body-xs font-medium text-text-tertiary hover:text-text-primary"
    >
      {expanded ? 'Show less' : `Show all ${total}`}
    </button>
  );
}

function UnmappedCard({ data }: { data: ToolCosts }) {
  const [expanded, setExpanded] = useState(false);
  if (data.unmapped.length === 0) return null;
  const visible = expanded ? data.unmapped : data.unmapped.slice(0, 10);

  return (
    <Card padding="md">
      <PanelTitle
        title={`Vendors not in the cost mapping (${data.unmapped.length})`}
        info="These have spend on a tool account but no mapping yet, so they're counted under whatever account Odoo booked them on. Assign one here and it sticks; no need to touch the mapping sheet. Card-paid tools with no Odoo supplier are matched on the name shown here."
      />
      <Table minWidth={640}>
        <THead>
          <tr>
            <Th>Vendor</Th>
            <Th>Booked on</Th>
            <Th align="right">Total (range)</Th>
            <Th>Belongs to</Th>
          </tr>
        </THead>
        <tbody>
          {visible.map((vendor) => (
            <UnmappedRow key={vendor.odooPartnerId ?? vendor.vendor} vendor={vendor} data={data} />
          ))}
        </tbody>
      </Table>
      {data.unmapped.length > 10 && (
        <ShowAllToggle expanded={expanded} total={data.unmapped.length} onToggle={() => setExpanded((v) => !v)} />
      )}
    </Card>
  );
}

/** Below this a mis-booking is rounding and FX noise, not something to fix in Odoo. */
const MISBOOKED_MIN_EUR = 25;

function MisbookedCard({ data }: { data: ToolCosts }) {
  const [expanded, setExpanded] = useState(false);
  const material = data.misbooked.filter((m) => Math.abs(m.total) >= MISBOOKED_MIN_EUR);
  const small = data.misbooked.length - material.length;
  if (material.length === 0) return null;
  const visible = expanded ? material : material.slice(0, 10);

  return (
    <Card padding="md">
      <PanelTitle
        title={`Bookings that don't match the cost mapping (${material.length})`}
        info="These vendors are booked on a different account than the cost mapping expects. The journal entry references make them easy to find and fix in Odoo. This view already counts them in the mapped category either way."
      />
      <Table minWidth={760} colWidths={['18%', '26%', '26%', '9rem', 'auto']}>
        <THead>
          <tr>
            <Th>Vendor</Th>
            <Th>Booked on</Th>
            <Th>Should be</Th>
            <Th align="right">Amount</Th>
            <Th>Journal entries</Th>
          </tr>
        </THead>
        <tbody>
          {visible.map((m) => (
            <Tr key={`${m.vendor}:${m.actualAccountCode}`}>
              <Td><VendorName raw={m.vendor} /></Td>
              {/* Account codes are references: the name shows, the code is in the tooltip. */}
              <Td className="text-text-secondary">
                <LedgerAccount
                  code={m.actualAccountCode}
                  name={m.actualAccountName}
                  english={englishFor(data, m.actualAccountCode)}
                />
              </Td>
              <Td className="text-text-secondary">
                <LedgerAccount
                  code={m.expectedAccountCode}
                  name={m.expectedAccountName}
                  english={englishFor(data, m.expectedAccountCode)}
                />
              </Td>
              <Td align="right" className="whitespace-nowrap tabular-nums font-medium">
                {money(m.total, data.currency)}
                <span className="text-body-xs text-text-tertiary font-normal"> · {m.count}×</span>
              </Td>
              {/* Every reference, wrapped: they are what you search for in Odoo. */}
              <Td className="text-body-xs text-text-tertiary break-words">{m.moveNames.join(', ')}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      {small > 0 && (
        <TableFooter>
          {small} smaller mis-booking{small === 1 ? '' : 's'} under {money(MISBOOKED_MIN_EUR, data.currency)} hidden
        </TableFooter>
      )}
      {material.length > 10 && (
        <ShowAllToggle expanded={expanded} total={material.length} onToggle={() => setExpanded((v) => !v)} />
      )}
    </Card>
  );
}

/**
 * The month's spend with its change on last month. A cost going down is good
 * news, so the pill's arrow follows the sign (↓ for a drop) while its colour
 * follows the meaning (green for a drop), via TrendPill's `tone`.
 */
function SpendTile({
  label,
  value,
  sub,
  delta,
  vs,
}: {
  label: string;
  value: string;
  sub: string;
  delta: number | null;
  vs: string | null;
}) {
  const direction = delta === null || delta === 0 ? 'neutral' : delta < 0 ? 'down' : 'up';
  const tone = direction === 'down' ? 'good' : direction === 'up' ? 'bad' : 'neutral';
  return (
    <Card padding="md">
      <div className="min-w-0 [container-type:inline-size]">
        <p className="eyebrow truncate">{label}</p>
        <p
          className="text-text-primary mt-3 tabular-nums font-light leading-none tracking-[-0.03em] text-[clamp(1.5rem,12cqi,2.5rem)]"
          title={value}
        >
          {value}
        </p>
        {delta !== null && vs && (
          <div className="mt-3">
            <TrendPill trend={direction} tone={tone}>
              {delta > 0 ? '+' : delta < 0 ? '−' : ''}
              {Math.abs(delta)}% vs {vs}
            </TrendPill>
          </div>
        )}
        <p className="text-body-xs text-text-tertiary mt-1">{sub}</p>
      </div>
    </Card>
  );
}

export function ToolCostsTab({ months }: { months: number }) {
  const [categoryFilter, setCategoryFilter] = useState<ToolCostCategoryId | 'all'>('all');
  const { data, isLoading, error, refetch } = useToolCosts(months);
  // Fixed baseline for the "unusual months" check; falls back to the chip's data while it loads.
  const { data: baselineData } = useToolCosts(BASELINE_MONTHS_SHOWN);
  useHealthNotes(data);

  if (error) {
    return <ErrorState title="Couldn’t load tool costs" error={error} onRetry={() => refetch()} />;
  }
  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        {/* Placeholder tiles hold the row's height, so the page doesn't jump when the numbers land. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {['Tool spend', 'Biggest category', FULLY_BOOKED_TILE].map((label) => (
            <TileSkeleton key={label} label={label} />
          ))}
        </div>
        <LoadingState height={CHART_HEIGHT.tall} />
      </div>
    );
  }

  // The reference month is the last one with every recurring bill booked, and
  // it is compared with the fully booked month before it — never with a month
  // still missing its OpenAI or AWS bill.
  const refMonth = data.insightsMonth ? data.series.find((p) => p.month === data.insightsMonth) : undefined;
  const refIdx = refMonth ? data.series.indexOf(refMonth) : -1;
  const prevMonth = refIdx > 0 ? [...data.series.slice(0, refIdx)].reverse().find(isFullyBooked) : undefined;
  const biggestCategory = refMonth
    ? data.categories.reduce((best, c) => (refMonth.byCategory[c.id] > refMonth.byCategory[best.id] ? c : best))
    : undefined;
  const delta =
    refMonth && prevMonth && prevMonth.total > 0
      ? Math.round(((refMonth.total - prevMonth.total) / prevMonth.total) * 100)
      : null;

  // Closed months after the reference month that still miss a recurring bill.
  const openClosed = data.series.filter(
    (p) => !isCurrentMonth(p.month) && !isFullyBooked(p) && (!refMonth || p.month > refMonth.month),
  );

  // Total and average over the same months: the fully booked full ones. What
  // the rest of the window holds (not fully booked, month to date) is said
  // beside it, never folded in.
  const avgMonths = bookedMonths(data);
  const bookedTotal = avgMonths.reduce((sum, p) => sum + p.total, 0);
  const windowTotal = data.series.reduce((sum, p) => sum + p.total, 0);
  const restTotal = windowTotal - bookedTotal;
  // The months of the window the tile leaves out: not fully booked, the
  // month to date, or a half first month.
  const bookedSet = new Set(avgMonths.map((p) => p.month));
  const restMonths = data.series.filter((p) => !bookedSet.has(p.month) && p.total !== 0).map((p) => p.month);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SpendTile
          label={refMonth ? `Tool spend, ${formatMonthKey(refMonth.month)}` : 'Tool spend'}
          value={refMonth ? money(refMonth.total, data.currency) : 'No fully booked month'}
          sub={
            refMonth
              ? `Last fully booked month${prevMonth ? `, vs ${formatMonthKey(prevMonth.month)}` : ''} · Odoo vendor bills`
              : `No month in this window has every recurring bill booked yet`
          }
          delta={delta}
          vs={prevMonth ? formatMonthKey(prevMonth.month) : null}
        />
        <StatTile
          label={refMonth ? `Biggest category, ${formatMonthKey(refMonth.month)}` : 'Biggest category'}
          value={refMonth && biggestCategory ? money(refMonth.byCategory[biggestCategory.id], data.currency) : '—'}
          sub={
            refMonth && biggestCategory && refMonth.total > 0
              ? `${biggestCategory.label} · ${Math.round((refMonth.byCategory[biggestCategory.id] / refMonth.total) * 100)}% of the month`
              : biggestCategory?.label
          }
        />
        <StatTile
          label={avgMonths.length > 0 ? `${avgMonths.length} fully booked month${avgMonths.length === 1 ? '' : 's'}` : FULLY_BOOKED_TILE}
          value={avgMonths.length > 0 ? money(bookedTotal, data.currency) : '—'}
          sub={
            avgMonths.length > 0
              ? `${monthSpan(avgMonths.map((p) => p.month))} · ${money(
                  bookedTotal / avgMonths.length,
                  data.currency,
                )} a month${
                  Math.abs(restTotal) >= 0.5 && restMonths.length > 0
                    ? ` · ${plusMoney(restTotal, data.currency)} in ${monthSpan(restMonths)}, not counted until fully booked`
                    : ''
                }`
              : 'No fully booked month in this window yet'
          }
          tooltip="Total and average cover the same months: the full months that are fully booked (any recurring bill still missing comes to under 2% of a usual month). Every vendor counts, the ones the table hides included, so the table footer's average is this one. Months still missing more, and the month to date, are added beside it rather than mixed in."
        />
      </div>

      {refMonth && (refMonth.missingBills?.length ?? 0) > 0 && (
        <p className="-mt-2 text-body-xs text-text-tertiary">
          {formatMonthKey(refMonth.month)} counts as fully booked: the bills still to come ({missingList(refMonth, data.currency)})
          are under 2% of a usual month.
        </p>
      )}
      {openClosed.map((p) => (
        <p key={p.month} className="-mt-2 text-body-xs text-text-tertiary">
          {formatMonthKey(p.month)} is not fully booked yet: no bill from {missingList(p, data.currency)}. The
          month is left out of the averages, comparisons and insights until those bills land.
          {likelyBookedLater(data.vendors, p).map((l) => (
            <Fragment key={l.vendor}>
              {' '}
              {vendorDisplayName(l.vendor)}&apos;s {formatMonthKey(p.month)} bill looks booked in {formatMonthKey(l.month)}:{' '}
              {formatMonthKey(l.month)} carries {money(l.amount, data.currency)}, about{' '}
              {Math.round(l.amount / l.expected)} times its usual {money(l.expected, data.currency)}.
            </Fragment>
          ))}
        </p>
      ))}

      <AttentionLine data={data} baseline={baselineData ?? data} />

      <InsightsCard data={data} />
      <MonthlyStackedChart data={data} categoryFilter={categoryFilter} />
      <VendorsCard data={data} baseline={baselineData ?? data} categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter} />
      {data.unmapped.length > 0 && (
        <div id="tool-unmapped" className="scroll-mt-4">
          <UnmappedCard data={data} />
        </div>
      )}
      {data.misbooked.some((m) => Math.abs(m.total) >= MISBOOKED_MIN_EUR) && (
        <div id="tool-misbooked" className="scroll-mt-4">
          <MisbookedCard data={data} />
        </div>
      )}
    </div>
  );
}
